import React, { useState, useEffect } from "react";
import { CustomBottomSheet } from "../../../../components/UI/CustomBottomSheet";
import { useDeleteCardMutation, useGetCardsQuery } from "../../../../providers/cards";
import PaymentCardLogo from "../../PaymentCardLogo";
import { walletOutline, closeCircleOutline, cardOutline, alertCircleOutline } from "ionicons/icons";
import { IonIcon, IonSkeletonText, useIonAlert } from "@ionic/react";

import styles from "./PaymentCardsList.module.scss";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import { useCustomAlert } from "../../../../components/CustomAlert";
import useSavePaymentCardMutation from "../../../../providers/payment/yookassa/useSavePaymentCardMutation";
import { useAuth } from "../../../../contexts/auth-context";
import { toast } from "react-toastify";
import { useLocation } from "react-router";
import { App } from "@capacitor/app";
import { AppLauncher } from "@capacitor/app-launcher";

const cx = classNames.bind(styles);

const cardsSkeleton = [...new Array(3).keys()];

const PaymentCardsList = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const userId = user?.id;
  const { mutate: deleteCardMutate } = useDeleteCardMutation();
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const { data: fetchedPaymentCards, isLoading: isCardsLoading, refetch: refetchPaymentCards } = useGetCardsQuery();
  const { customPresentAlert, closeAlert, AlertComponent } = useCustomAlert();
  const [presentAlert] = useIonAlert();

  const { mutateAsync: savePaymentCardMutationAsync, isPending: isSavingCard } = useSavePaymentCardMutation();

  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    const listenerPromise = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        refetchPaymentCards();
      }
    });

    return () => {
      listenerPromise.then((listener) => listener.remove());
    };
  }, [refetchPaymentCards]);

  const handleAddCard = async () => {
    customPresentAlert({
      header: t("1rub"),
      message: t("Link card text"),
      buttons: [
        { text: t("Cancel"), role: "cancel" },
        {
          text: t("Link a card"),
          handler: async () => {
            if (!userId) return;

            try {
              const response = await savePaymentCardMutationAsync({
                route: pathname,
              });

              if (!response.confirmation?.confirmation_url) {
                throw new Error("No confirmation URL");
              }

              const responseConfirmationUrl = response.confirmation.confirmation_url;

              const paymentUrl = `${process.env.REACT_APP_PUBLIC_PAYMENTS_URL}/?confirmationUrl=${responseConfirmationUrl}`;

              console.log("paymentUrl", paymentUrl);

              await AppLauncher.openUrl({ url: paymentUrl });
            } catch (error: any) {
              console.error(error);
              toast.error(t("Error saving card: " + error.message));
            }
          },
        },
      ],
    });
  };

  const handleDeleteCard = (cardId: string) => {
    customPresentAlert({
      backdropDismiss: false,
      keyboardClose: false,
      header: t("Delete Card"),
      message: t("Are you sure you want to delete this card?"),
      buttons: [
        {
          text: t("Cancel"),
          role: "cancel",
        },
        {
          text: t("Delete"),
          handler: () => {
            setDeletingCardId(cardId);
            deleteCardMutate(
              { id: cardId },
              {
                onSuccess: () => {
                  refetchPaymentCards();
                  setDeletingCardId(null);
                },
                onError: () => {
                  setDeletingCardId(null);
                },
              },
            );
          },
        },
      ],
    });
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
    closeAlert();
  };

  const renderPreview = () => {
    return (
      <div className="flex text-center items-center justify-center rounded-xl h-[40px]  py-1 bg-gray-100 text-sm">
        <IonIcon icon={walletOutline} className="w-4 h-4 text-indigo-500 mr-2" />
        {t("Cards")}
      </div>
    );
  };

  return (
    <CustomBottomSheet
      open={isOpen}
      onDismiss={handleDismiss}
      showDefaultHeader={false}
      onPreviewClick={handleOpen}
      renderPreview={renderPreview}
      contentClassName={cx("bottom_sheet")}
      snapPoints={({ maxHeight }) => [maxHeight * 0.5, maxHeight * 0.75, maxHeight]}
      previewClassName={cx(
        "flex items-center justify-center rounded-xl h-[1.75rem] lg:h-full xl:w-[29%] px-3 py-1 bg-gray-100 text-sm",
      )}
      footer={
        <button
          onClick={handleAddCard}
          className="w-full flex items-center justify-center gap-2 bg-primary font-semibold p-3.5 rounded-3xl"
        >
          <IonIcon icon={cardOutline} />
          <span>{t("Add a card")}</span>
        </button>
      }
    >
      <AlertComponent />
      <div className="flex flex-col gap-2.5 h-full">
        {isCardsLoading ? (
          <>
            {cardsSkeleton.map((cardSkelet) => {
              return (
                <div key={cardSkelet} className="flex items-center justify-between">
                  <div className="flex gap-2.5">
                    <IonSkeletonText animated className="m-0 w-12 h-8 rounded-3xl" />
                    <IonSkeletonText animated className="m-0 w-36 h-8 rounded-sm" />
                  </div>
                  <IonSkeletonText animated className="m-0 w-6 h-6 rounded-full" />
                </div>
              );
            })}
          </>
        ) : (
          <>
            {!fetchedPaymentCards?.length ? (
              <div className="flex flex-col items-center justify-center text-center gap-4 h-full">
                <div className="font-semibold flex items-center justify-center gap-2">
                  <IonIcon icon={alertCircleOutline} />
                  <span>{t("No cards found")}</span>
                </div>
              </div>
            ) : (
              <>
                {fetchedPaymentCards?.map((card) => {
                  const isCardDeleting = deletingCardId === card.id;
                  return (
                    <div
                      key={card.id}
                      className={cx(
                        "flex justify-between items-center gap-2 cursor-pointer",
                        isCardDeleting && "opacity-50",
                      )}
                    >
                      <PaymentCardLogo cardType={card.cardType} cardLastFour={card.cardLastFour} />
                      <button disabled={isCardDeleting} onClick={() => handleDeleteCard(card.id)}>
                        <IonIcon icon={closeCircleOutline} color="danger" />
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </CustomBottomSheet>
  );
};

export default PaymentCardsList;
